import {
  HALF_TIME,
  HALF_TIME_BREAK,
  FOOTBALL,
  E_FUTBOL,
  E_BASKETBOL,
  AMERICAN_FOOTBALL,
  BASKETBALL,
  TENNIS,
  HANDBALL,
  PENALTY
} from 'Store/constants/sportTypes';

/**
 * Penaltı Kontrolü
 */
const isPenalty = type => type === PENALTY;

/**
 * Devre arasi kontrolu - HALF_TIME veya HALF_TIME_BREAK
 */
const isHalfTimeBreak = type => type === HALF_TIME || type === HALF_TIME_BREAK;

/**
 * Bu liste sadece default case'e düşen ve skor gösterilmemesi gereken
 * spor tipleri için kullanılır.
 */
const EXCLUDED_SPORT_TYPES = [
  AMERICAN_FOOTBALL, // Amerikan Futbolu'nda skor gösterimi istemiyoruz
  BASKETBALL,
  TENNIS,
  // Buraya default case'e giren ve skor gösterilmesini istemediğiniz
  // diğer spor tiplerini ekleyin
  // Örnek: TENNIS, HANDBALL, ICE_HOCKEY, MOTORSPORTS, SNOOKER, PINGPONG, MMA vb.
];

/**
 * Skor objesinin geçerli olup olmadığını kontrol eder
 * @param {Object} score - Skor objesi (home, away içermeli)
 * @param {number} [sportId] - Spor tipi ID'si (opsiyonel, bazı sporları exclude etmek için)
 * @returns {boolean} - Skor geçerli ise true, değilse false
 */
export const isScoreValid = (score, sportId = null) => {
  if (sportId != null && EXCLUDED_SPORT_TYPES.includes(sportId)) {
    return false;
  }
  return score != null && (score.home != null || score.away != null);
};

/**
 * Football/E-Futbol/E-Basketbol için özel render fonksiyonu
 * HALF_TIME/HALF_TIME_BREAK durumunda title gösterir, normal durumda time gösterir
 */
const renderFootballTimeStatus = score => {
  if (!score) return '';

  if (isHalfTimeBreak(score.type) && score.title) return score.title;
  return score.time || '';
};

/**
 * Generic/default render fonksiyonu
 * Tüm sporlar için varsayılan gösterim mantığı
 */
const renderGenericStatus = score => {
  if (!score) return '';

  // Devre arasi kontrolu - HALF_TIME veya HALF_TIME_BREAK
  if (isHalfTimeBreak(score.type) && score.title) return score.title;
  // title ve time'ı cache'le (gereksiz yeniden hesaplamayı önle)
  const title = score.title ? score.title.trim() : '';
  const time = score.time ? score.time.trim() : '';

  if (!title) return time;

  if (!time) return title;

  // Her ikisi de var - aynı mı kontrol et (case-insensitive)
  // toLowerCase() string allocation yapar, sadece gerektiğinde kullan
  if (title.length === time.length) {
    // Uzunluk aynıysa toLowerCase ile kontrol et
    if (title.toLowerCase() === time.toLowerCase()) return title;
  }

  // Farklıysa birleştir
  return `${title} ${time}`;
};

/**
 * Sport type'a göre özel render fonksiyonlarının mapping'i
 * Sadece özel gösterime ihtiyaç duyan spor tipleri için tanımlanır
 * Tanımlı olmayan tipler için renderGenericStatus kullanılır
 *
 * @example
 * // Yeni bir branş için özel render eklemek:
 * SPORT_TYPE_RENDER_MAP[NEW_SPORT_TYPE] = customRenderFunction;
 */
/**
 * Tenis için sadece title döndüren renderer (EventRow/Live için)
 */
const renderTennisStatus = score => {
  if (!score) return '';
  return score.title || '';
};

const renderBasketballStatus = score => {
  if (!score) return '';

  if (score.type === HALF_TIME && score.title) {
    return [score.title.trim()];
  }

  const time = score.time?.trim();
  const title = score.title?.trim();

  if (!time && !title) return '';

  return [time, title].filter(Boolean);
};


const renderHandballStatus = (score, scores = []) => {
  if (!score) return '';

  const halfScore = Array.isArray(scores)
    ? scores.find(item => item?.type === 'ht' || (item?.title || '').trim().toUpperCase() === 'IY')
    : null;

  if (halfScore) {
    const isFirstHalf = score.home === halfScore.home && score.away === halfScore.away;
    return isFirstHalf ? '1.Yarı' : '2.Yarı';
  }

  return score.title || score.time || '';
};

/**
 * Status gösterimi için sport type mapping (EventRow/Live için)
 * Tenis dahil tüm sporlar için status bilgisi döner
 */
const SPORT_TYPE_RENDER_MAP = {
  [FOOTBALL]: renderFootballTimeStatus,
  [E_FUTBOL]: renderFootballTimeStatus,
  [E_BASKETBOL]: renderFootballTimeStatus,
  [HANDBALL]: renderHandballStatus,
  [TENNIS]: renderTennisStatus, // Tenis için sadece title gösterilir
  // Yeni branşlar için özel render fonksiyonları buraya eklenebilir
  // Tanımlı olmayan tipler otomatik olarak renderGenericStatus kullanır
};

const SPORT_TYPE_RENDER_MAP_V2 = {
  [FOOTBALL]: renderFootballTimeStatus,
  [E_FUTBOL]: renderFootballTimeStatus,
  [E_BASKETBOL]: renderFootballTimeStatus,
  [HANDBALL]: renderHandballStatus,
  [TENNIS]: renderTennisStatus,
  [BASKETBALL]: renderBasketballStatus
  // Yeni branşlar için özel render fonksiyonları buraya eklenebilir
  // Tanımlı olmayan tipler otomatik olarak renderGenericStatus kullanır
};
/**
 * Timer gösterilmemesi gereken sporlar listesi
 * Bu sporlar için EventTimerLabel'da timer null olacak
 */
const TIMER_EXCLUDED_SPORT_TYPES = [
  TENNIS,
  // Diğer timer gösterilmemesi gereken sporlar buraya eklenebilir
];

/**
 * Skor durumunu (status) dinamik olarak render eder
 * Sport type'a göre özel render fonksiyonu varsa onu kullanır,
 * yoksa generic render fonksiyonunu kullanır
 * EventRow/Live gibi status gösterilmesi gereken componentler için kullanılır
 *
 * @param {Object} score - Skor objesi (type, title, time içerebilir)
 * @param {number} [sportType] - Sport type ID (opsiyonel, özel render için gerekli)
 * @returns {string} - Gösterilecek status metni
 */
export const renderScoreStatus = (score, sportType = null, scores = null) => {
  if (!score) return '';

  if (isPenalty(score.type) && score.title) return score.title;

  // Özel render fonksiyonu varsa direkt kullan
  const renderer = sportType != null ? SPORT_TYPE_RENDER_MAP[sportType] : null;

  // Renderer varsa kullan, yoksa generic
  return renderer ? renderer(score, scores) : renderGenericStatus(score);
};

export const renderScoreStatusV2 = (score, sportType = null) => {
  if (!score) return '';

  if (isPenalty(score.type) && score.title) return score.title;

  // Özel render fonksiyonu varsa direkt kullan
  const renderer = sportType != null ? SPORT_TYPE_RENDER_MAP_V2[sportType] : null;

  // Renderer varsa kullan, yoksa generic
  return renderer ? renderer(score) : renderGenericStatus(score);
};

/**
 * Timer durumunu render eder (EventTimerLabel için)
 * Bazı sporlar için timer gösterilmez (örn: Tenis)
 *
 * @param {Object} score - Skor objesi (type, title, time içerebilir)
 * @param {number} [sportType] - Sport type ID
 * @returns {string} - Gösterilecek timer metni (excluded sporlar için boş string)
 */
export const renderTimerStatus = (score, sportType = null) => {
  if (!score) return '';

  // Timer gösterilmemesi gereken sporlar için boş döndür
  if (sportType != null && TIMER_EXCLUDED_SPORT_TYPES.includes(sportType)) {
    return '';
  }

  // Diğer sporlar için normal render
  const renderer = sportType != null ? SPORT_TYPE_RENDER_MAP[sportType] : null;
  return renderer ? renderer(score) : renderGenericStatus(score);
};

/**
 * Yeni bir branş için özel render fonksiyonu kaydetmek için utility fonksiyon
 * Bu fonksiyon ile runtime'da da yeni render fonksiyonları eklenebilir
 *
 * @param {number} sportType - Sport type ID
 * @param {Function} renderFunction - Render fonksiyonu (score => string)
 *
 * @example
 * // Yeni bir branş için özel render eklemek:
 * registerSportTypeRenderer(NEW_SPORT_TYPE, (score) => {
 *   // Özel gösterim mantığı
 *   return score.customField || '';
 * });
 */
export const registerSportTypeRenderer = (sportType, renderFunction) => {
  if (typeof renderFunction !== 'function') {
    console.warn(`[scoreDisplayHelper] registerSportTypeRenderer: renderFunction must be a function for sportType ${sportType}`);
    return;
  }
  SPORT_TYPE_RENDER_MAP[sportType] = renderFunction;
};

